import { createContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { fetchFeatureFlags, type LabsFlagInfo } from '@/services/featureFlagsService'
import type { FeatureFlags } from '@/types/featureFlags'

export interface FeatureFlagContextValue {
    flags: FeatureFlags
    isAdmin: boolean
    isLoading: boolean
    labsFlags: LabsFlagInfo[]
}

export const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined)

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
    const { session } = useAuth()
    const { selectedProtocol } = usePlatform()

    const agent = useMemo(() => (session ? new Agent(session) : null), [session])

    const { data, isLoading } = useQuery({
        queryKey: ['featureFlags', session?.did],
        queryFn: () => fetchFeatureFlags(agent!),
        enabled: !!agent,
        staleTime: 30 * 60 * 1000, // 30 minutes
        retry: false,
    })

    const value = useMemo<FeatureFlagContextValue>(
        () => ({
            flags: (data?.flags ?? {}) as FeatureFlags,
            // Admin/feature-flag access is an AT Protocol account concept only (the
            // `getFlags` call is authenticated with a Bluesky service-auth token), so
            // isAdmin must also require Bluesky to be the selected protocol — otherwise
            // admin-only UI (e.g. Settings > Developer) can leak through while a
            // Mastodon account is selected but an admin ATP session still exists.
            isAdmin: selectedProtocol === 'bluesky' && (data?.isAdmin ?? false),
            isLoading: !!agent && isLoading,
            labsFlags: data?.labsFlags ?? [],
        }),
        [data, isLoading, agent, selectedProtocol]
    )

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>
}
